import { z } from "zod";

/**
 * Shared DTO cho Admin Transparency Link Ops + Quiet-hours VN (US-AE13.F2-0045 · @UC9121
 * transparency-link-ops · @UC9122 quiet-hours-sla-vn). MỘT nguồn sự thật cho api (S02/S03), worker
 * enforce (S04), admin web (S06).
 *
 * Oversight TOÀN HỆ (khác owner-scope E10-0063): admin list link public active mọi nhóm + revoke +
 * audit. Quiet-hours VN (Asia/Ho_Chi_Minh): cấu hình khung giờ hoãn push/ZNS ở dispatcher.
 *
 * BẤT BIẾN:
 * - REUSE TransparencyLink (E10-0063) — KHÔNG rebuild token/CRUD. Admin chỉ đọc + revoke.
 * - AC2 conversion (views) DEFERRED — TransparencyLink KHÔNG có field views (KHÔNG bịa metric).
 * - Quiet-hours enforce ở dispatcher (S04), KHÔNG ở producer. in_app KHÔNG hoãn.
 * - `.strict()` chống over-post. C2 no-PII (link ref id/hash-prefix, KHÔNG token plaintext).
 */

// ============================================================
// Transparency link oversight
// ============================================================

/** Trạng thái link (derived): active | revoked | expired. */
export const transparencyLinkStatusSchema = z.enum(["active", "revoked", "expired"]);
export type TransparencyLinkStatus = z.infer<typeof transparencyLinkStatusSchema>;

/**
 * 1 dòng link oversight. `id` = link id; `groupId` con trỏ; `scopeSummary` = mô tả phạm vi dữ liệu
 * (KHÔNG token plaintext — chỉ hash-prefix/id). status derived. `views` KHÔNG có — và cột UI tương
 * ứng đã BỎ ở 0374-S04 (chưa từng có nguồn đếm; cột luôn rỗng là cột giả vờ).
 */
export const transparencyLinkItemSchema = z
  .object({
    id: z.string(),
    groupId: z.string(),
    scopeSummary: z.string(),
    status: transparencyLinkStatusSchema,
    expiresAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * 🔴 US-AE16.F6-0374-S04 (@UC9650) — AI TẠO link minh bạch.
     *
     * Link minh bạch là thứ **phơi dữ liệu tài chính của nhóm ra NGOÀI nhóm**. Không biết ai tạo thì
     * khi có sự cố rò rỉ **không truy được** — mà đây đúng là loại sự cố cần truy.
     *
     * `null` có HAI nghĩa, cố ý không phân biệt ở đây vì cả hai đều dẫn tới cùng cách hiển thị "—":
     * link cũ trước khi cột được ghi, hoặc vai này không được xem danh tính (`canViewPii`).
     */
    createdByName: z.string().nullable(),
  })
  .strict();

export type TransparencyLinkItem = z.infer<typeof transparencyLinkItemSchema>;

/** Envelope list cursor. */
export const transparencyLinkListResponseSchema = z
  .object({
    data: z.array(transparencyLinkItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type TransparencyLinkListResponse = z.infer<typeof transparencyLinkListResponseSchema>;

export const TRANSPARENCY_LIST_MAX_LIMIT = 100;

/** Query list — filter status, cursor + limit. */
export const transparencyLinkListQuerySchema = z
  .object({
    status: transparencyLinkStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(TRANSPARENCY_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type TransparencyLinkListQuery = z.infer<typeof transparencyLinkListQuerySchema>;

export const REVOKE_REASON_MIN_LENGTH = 1;
export const REVOKE_REASON_MAX_LENGTH = 2000;

/** Body revoke — reason bắt buộc non-empty (audit). `.strict()`. */
export const revokeTransparencyLinkSchema = z
  .object({
    reason: z.string().trim().min(REVOKE_REASON_MIN_LENGTH).max(REVOKE_REASON_MAX_LENGTH),
  })
  .strict();

export type RevokeTransparencyLinkBody = z.infer<typeof revokeTransparencyLinkSchema>;

// ============================================================
// Quiet-hours config (Asia/Ho_Chi_Minh)
// ============================================================

/** Key system_settings lưu quiet-hours + default sensible (SD-9). */
export const QUIET_HOURS_SETTING_KEY = "comms.quiet_hours";

/** Timezone VN mặc định (khớp DEFAULT_GROUP_TIMEZONE domain). */
export const QUIET_HOURS_DEFAULT_TIMEZONE = "Asia/Ho_Chi_Minh";

/**
 * Cấu hình quiet-hours: khung giờ [startHour, endHour) theo timezone → hoãn push/ZNS. Cho phép wrap qua
 * nửa đêm (start > end, vd 22→7). Hour ∈ [0,24). timezone IANA. `.strict()`.
 */
export const quietHoursConfigSchema = z
  .object({
    startHour: z.number().int().min(0).max(23),
    endHour: z.number().int().min(0).max(23),
    timezone: z.string().min(1).default(QUIET_HOURS_DEFAULT_TIMEZONE),
    enabled: z.boolean().default(true),
  })
  .strict();

export type QuietHoursConfig = z.infer<typeof quietHoursConfigSchema>;

/** Default v1: 22:00–07:00 Asia/Ho_Chi_Minh, bật. */
export const QUIET_HOURS_DEFAULT: QuietHoursConfig = {
  startHour: 22,
  endHour: 7,
  timezone: QUIET_HOURS_DEFAULT_TIMEZONE,
  enabled: true,
};

/** Body PATCH /admin/comms/quiet-hours (= quietHoursConfigSchema; SUPER_ADMIN). */
export const patchQuietHoursSchema = quietHoursConfigSchema;
export type PatchQuietHoursBody = QuietHoursConfig;
